import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllUsersTodos } from "../../../store/thunks/todoThunk";
import ToDoModal from "../../../modals/ToDoModal";
import {
  setSelectedUser,
  clearSelectedUser,
} from "../../../store/slices/userSlice";

const UserToDoList = React.memo(() => {
  const dispatch = useDispatch();
  const allUsersTodos = useSelector((state) => state.todos.allUsersTodos);
  const loadingAllUsersTodos = useSelector(
    (state) => state.todos.loadingAllUsersTodos
  );
  const selectedUser = useSelector((state) => state.users.selectedUser);
  const [showToDoModal, setShowToDoModal] = useState(false);

  // Fetch all user todos on mount
  useEffect(() => {
    dispatch(fetchAllUsersTodos());
  }, [dispatch]);

  // Define all categories
  const allCategories = useMemo(
    () => [
      "Treat",
      "Soap", 
      "Approve SOAP",
      "Confirm",
      "Invoice",
      "Payment",
      "Schedule",
      "Reschedule",
    ],
    []
  );

  // Transform todos into table rows
  const userRows = useMemo(() => {
    if (!allUsersTodos) return [];
    return Object.entries(allUsersTodos).map(([userId, userData]) => ({
      userId,
      firstName: userData.userDetails.firstName,
      lastName: userData.userDetails.lastName,
      role: userData.userDetails.role,
      todos: userData.todos,
    }));
  }, [allUsersTodos]);

  // Handle row click
  const handleUserClick = (userRow) => {
    if (!selectedUser || selectedUser._id !== userRow.userId) {
      dispatch(
        setSelectedUser({
          _id: userRow.userId,
          firstName: userRow.firstName,
          lastName: userRow.lastName,
          role: userRow.role,
        })
      );
      setShowToDoModal(true);
    }
  };

  if (!allUsersTodos) return <div>No todos available</div>;

  return (
    <>
      <div className="mt-8 relative">
        {loadingAllUsersTodos && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}
        <div className="bg-white border rounded-sm shadow-sm">
          <div className="px-4 h-14 flex items-center border-b">
            <h3 className="text-base font-semibold text-gray-900">
              To-Do List
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50/50">
                  <th className="px-4 h-10 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    User
                  </th>
                  {allCategories.map((category) => (
                    <th
                      key={category}
                      className="px-4 h-10 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {category}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {userRows.map((userRow) => (
                  <tr
                    key={userRow.userId}
                    onClick={() => handleUserClick(userRow)}
                    className="border-b last:border-0 hover:bg-gray-50/50 cursor-pointer transition-colors">
                    <td className="px-4 h-10 text-sm font-medium text-gray-900">
                      {`${userRow.firstName} ${userRow.lastName}`}
                    </td>
                    {allCategories.map((category) => (
                      <td
                        key={`${userRow.userId}-${category}`}
                        className="px-4 h-10 text-sm text-gray-600">
                        {userRow.todos[category]?.total || 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showToDoModal && (
        <ToDoModal
          onClose={() => {
            setShowToDoModal(false);
            dispatch(clearSelectedUser());
          }}
        />
      )}
    </>
  );
});

export default UserToDoList;
